<template>
  <div class="mx-auto mw-700">
    <v-spacer class="mt-2" />

    <h3>
      {{ $t('host.controlAccess.singlePayment.title') }}
    </h3>

    <v-btn
      v-if="!openedForm"
      @click="openedForm = !openedForm"
      color="primary"
      elevation="0"
      class="mt-5"
    >
      {{ $t('host.controlAccess.singlePayment.createSinglePayment') }}
    </v-btn>

    <v-spacer class="my-3" />

    <v-form
      v-if="openedForm"
      @submit.prevent="validate"
      ref="singlePaymentForm"
      lazy-validation
    >
      <v-row>
        <v-col cols="12" sm="4">
          <v-text-field
            v-model.lazy="amount"
            v-money="catalogCurrency()"
            :loading="loading"
            :label="$t('host.controlAccess.singlePayment.form.amount')"
            class="amount inputAmount"
            hide-details="auto"
            ref="amount"
            outlined
          />
        </v-col>
      </v-row>

      <v-spacer class="my-3" />

      <v-textarea
        v-model="description"
        :rules="[rules.required, rules.minLength, rules.maxLength]"
        :label="$t('host.controlAccess.singlePayment.form.observations')"
        outlined
      />

      <div class="d-flex justify-end">
        <v-btn
          @click="openedForm = false"
          :disabled="loading"
          color="grey darken-2"
          class="mr-3"
          text
        >
          {{ $t('host.controlAccess.cancel') }}
        </v-btn>

        <v-btn
          @click="validate"
          :loading="loading"
          color="primary"
          elevation="0"
        >
          {{ $t('host.controlAccess.singlePayment.createSinglePayment') }}
        </v-btn>
      </div>
    </v-form>

    <v-divider class="my-8" />

    <v-data-table
      :headers="headers"
      :items="hostPayments"
      :no-data-text="$t('appointment.table.noData')"
      :loading-text="$t('appointment.table.loading')"
      :items-per-page="10"
      :footer-props="{
        'items-per-page-text': '',
        'items-per-page-options': [],
      }"
      mobile-breakpoint="20"
      class="elevation-0"
      item-key="id"
      single-expand
      disable-sort
      show-expand
    >
      <template v-slot:[`item.payment_type`]="{ item }">
        {{
          item.payment_type
            ? $t(`host.label.payments.types.${item.payment_type}`)
            : '---'
        }}
      </template>
      <template v-slot:[`item.created_at`]="{ item }">
        {{ item.created_at ? formatDate(item.created_at) : '---' }}
      </template>
      <template v-slot:[`item.transaction_id`]="{ item }">
        <v-chip class="body-2">
          <small>{{ item.id }}</small>
          <CopyToClipboard :value="item.id" />
        </v-chip>
      </template>
      <template v-slot:[`item.total`]="{ item }">
        <span :class="`${item.total < 0 ? 'red--text' : 'green--text'}`">
          {{ item.total ? formatMoney(item.total) : '---' }}
        </span>
      </template>
      <template v-slot:[`item.transfer_status`]="{ item }">
        {{ $t(`host.label.payments.status.${item.transfer_status}`) }}
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-0 ma-0">
          <v-card color="grey lighten-4" class="pa-5" elevation="0" tile>
            <h6 class="text-uppercase grey--text">
              {{ $t('host.label.description') }}
            </h6>
            {{ item.description ? item.description : '---' }}
          </v-card>
        </td>
      </template>
    </v-data-table>

    <h-pagination
      v-if="hostPayments.length >= 1"
      :fetch="() => fetchHostPayments(this.currentHostId)"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { catalogDate, catalogMonetary } from '@/util/catalog'
import { hostPaymentsHeaders } from '@/views/listHeaders/hostHeaders'
import { required, minLength } from '@/util/errorHandling'
import { catalogCurrency } from '../../util/catalog'
import { clearCurrency } from '../../util/helpers'
import { VMoney } from 'v-money'

import HAmount from '../app/HAmount'
import HTextInput from '../app/HTextInput'
import CopyToClipboard from '@/components/app/CopyToClipboard'
import HDraggable from '@/components/app/HDraggable'

export default {
  name: 'HostSinglePayment',

  components: {
    HAmount,
    HDraggable,
    HTextInput,
    CopyToClipboard,
  },

  directives: {
    money: VMoney
  },

  props: {
    hostId: {
      type: [String, Number],
      required: false,
    },
  },

  watch: {
    hostId(id) {
      if (id) this.fetchHostPayments(id)
      else this.fetchHostPayments(this.host.id)
    },
  },

  data() {
    return {
      amount: 0,
      valid: false,
      loading: false,
      openedForm: false,
      description: '',
      headers: hostPaymentsHeaders,
      formatMoney: catalogMonetary,
      formatDate: catalogDate,
      rules: {
        required,
        minLength,
        maxLength: (v) => v.length <= 300 || 'Máximo de 300 characters',
      },

      catalogCurrency,
    }
  },

  computed: {
    ...mapGetters(['hostPayments', 'isLoading', 'host']),

    currentHostId() {
      return this.host.id ?? this.hostId
    },
  },

  methods: {
    ...mapActions(['fetchHostPayments', 'createHostPayment']),

    resetValue() {
      this.amount = 0
    },

    validate() {
      if (!this.$refs.singlePaymentForm.validate()) return

      this.loading = true

      const body = {
        host_id: this.currentHostId,
        description: this.description,
        value: clearCurrency(this.amount),
      }

      Promise.resolve(this.createHostPayment(body))
        .then(() => {
          this.openedForm = false
          this.description = ''
          this.amount = 0
        })
        .then(() => {
          this.resetValue()
        })
        .finally(() => {
          this.loading = false
        })
    },
  },

  mounted() {
    this.fetchHostPayments(this.currentHostId)
  },
}
</script>
